import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Send, ExternalLink, ArrowLeft, MessageCircle, Smartphone } from 'lucide-react';

/**
 * Multi-channel OTP verification step (used by signup + forgot-password).
 * Displays every channel the tenant admin has enabled — the same 6-digit
 * code was fanned-out to WhatsApp, SMS and/or Telegram concurrently by the
 * backend. The user just needs to grab the code from ANY of them.
 *
 *  props.info  = { channels: string[], deep_link?, bot_username?, fullMobile }
 *  props.onVerify(code) — async
 *  props.onBack
 */
export default function TelegramOtpStep({ info, onVerify, onBack }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const channels = info?.channels || [];
  const has = (k) => channels.includes(k);

  const submit = async (e) => {
    e.preventDefault();
    if (code.length < 4) return;
    setBusy(true);
    try { await onVerify(code); } catch (e) { /* toast handled upstream */ }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="telegram-otp-step">
      <div className="rounded-lg bg-zinc-900/70 border border-zinc-800 p-4 space-y-3">
        <div className="text-sm text-zinc-300 leading-relaxed">
          A 6-digit verification code was sent to <span className="text-zinc-100 font-semibold">+{info?.fullMobile}</span> via:
        </div>

        <div className="flex flex-wrap gap-2">
          {has('whatsapp') && (
            <span className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md bg-[#25d366]/20 border border-[#25d366]/40 text-[#25d366] text-xs font-bold" data-testid="channel-badge-whatsapp">
              <MessageCircle size={13} /> WhatsApp
            </span>
          )}
          {has('sms') && (
            <span className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md bg-amber-500/20 border border-amber-700/40 text-amber-300 text-xs font-bold" data-testid="channel-badge-sms">
              <Smartphone size={13} /> SMS
            </span>
          )}
          {has('telegram') && (
            <span className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md bg-[#2ca5e0]/20 border border-[#2ca5e0]/40 text-[#2ca5e0] text-xs font-bold" data-testid="channel-badge-telegram">
              <Send size={13} /> Telegram
            </span>
          )}
        </div>

        {has('telegram') && info?.deep_link && (
          <a href={info.deep_link} target="_blank" rel="noreferrer" data-testid="telegram-open-link"
            className="w-full h-11 rounded-md bg-[#2ca5e0] hover:bg-[#1f8fc4] text-white font-bold text-[14px] flex items-center justify-center gap-2 transition">
            <ExternalLink size={16} /> Open @{info.bot_username || 'TelegramBot'}
          </a>
        )}

        {channels.length === 0 && (
          <div className="text-amber-400/90 text-xs" data-testid="otp-channels-warning">
            No delivery channel could send your OTP. Please contact the site admin.
          </div>
        )}
      </div>

      <div>
        <label className="text-[13px] font-semibold text-zinc-200 mb-1.5 block">Enter 6-digit Code</label>
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="123456"
          className="h-12 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 text-center tracking-[0.6em] text-lg font-bold"
          autoFocus
          data-testid="otp-code-input"
        />
      </div>

      <button type="submit" disabled={busy || code.length < 4} className="w-full h-11 rounded-md bg-[color:var(--gold)] hover:brightness-110 disabled:opacity-50 text-black font-bold transition" data-testid="otp-verify-btn">
        {busy ? 'Verifying…' : 'Verify & Continue'}
      </button>

      <div className="flex items-center justify-between text-xs text-zinc-400 pt-1">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 hover:text-zinc-200" data-testid="otp-back-btn">
          <ArrowLeft size={14} /> Back
        </button>
      </div>
    </form>
  );
}
